import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { Article, Category } from '@wix/answers-api';
import { CategoryList } from '../CategoryList';
import { CategoryArticles } from '../CategoryArticles';
import { Divider } from '@wix/design-system';
import { useHttpClient } from '@wix/fe-essentials-standalone';
import { Loader } from '../Loader';
import { pushUrl } from '../InnerLink';
import { Context } from '../../context';
import css from './index.module.scss';

type DeprecatedCategoryPageProps = {
  category?: Category;
  subCategory?: Category;
  categories: Category[];
  categoryArticles?: Article[];
};

/** @deprecated use the new CategoryPageWrapper */
export const DeprecatedCategoryPage: FunctionComponent<
  DeprecatedCategoryPageProps
> = ({ category, subCategory, categories, categoryArticles }) => {
  const { locale } = useRouter();
  const httpClient = useHttpClient();
  const { isProductionDomain, pageTemplateData, setPageTemplateData } =
    useContext(Context);
  const [selectedCategory, setSelectedCategory] = useState<
    Category | undefined
  >(category);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState<
    Category | undefined
  >(subCategory);
  const [selectedCategoryArticles, setSelectedCategoryArticles] = useState<
    Article[]
  >(categoryArticles || []);

  const updateCategoryUrl = (url: string) =>
    pushUrl(url, isProductionDomain, true);

  const getArticles = useCallback(
    async (categoryId: string, returnData?: boolean) => {
      if (!returnData) {
        setIsLoading(true);
      }
      const { data } = await httpClient.get<{ [key: string]: Article[] }>(
        `/api/category/articles`,
        {
          params: {
            locale,
            summarize: true,
            categoryIds: [categoryId],
          },
        }
      );
      if (!returnData) {
        setIsLoading(false);
        setSelectedCategoryArticles(data[categoryId]);
      } else {
        return data[categoryId];
      }
    },
    [httpClient, locale]
  );

  const onChangeSubCategory = async (newSubCategory: Category) => {
    setPageTemplateData({
      ...pageTemplateData,
      itemId: newSubCategory.id,
      pageData: { category: newSubCategory },
    });
    setSelectedSubCategory(newSubCategory);
    await getArticles(newSubCategory.id);
    await updateCategoryUrl(`/${locale}${newSubCategory.uri}`);
  };

  const onChangeParentCategory = async (newParentCategory: Category) => {
    setSelectedCategory(newParentCategory);
    const { children } = newParentCategory;
    const hasChildren = children && children.length > 0;
    setPageTemplateData({
      ...pageTemplateData,
      itemId: hasChildren ? children[0].id : newParentCategory.id,
      pageData: { category: hasChildren ? children[0] : newParentCategory },
    });
    setSelectedSubCategory(hasChildren ? children[0] : undefined);
    await getArticles(hasChildren ? children[0].id : newParentCategory.id);
    await updateCategoryUrl(
      `/${locale}${hasChildren ? children[0].uri : newParentCategory.uri}`
    );
  };

  return (
    <>
      <div className={css.categoryPage}>
        {selectedCategory && categories && categories.length > 0 && (
          <CategoryList
            category={selectedCategory as Category}
            subCategory={selectedSubCategory}
            categories={categories}
            changeSubCategory={onChangeSubCategory}
            changeParentCategory={onChangeParentCategory}
          />
        )}
        <Divider className={css.divider} skin="light" direction="vertical" />
        <Loader isLoading={isLoading} />
        {selectedCategory && (
          <CategoryArticles
            category={selectedCategory}
            subCategory={selectedSubCategory}
            categoryArticles={selectedCategoryArticles}
          />
        )}
      </div>
    </>
  );
};
